import { h } from 'preact';

// Progress scene shown while the convention admin panel talks to NSF/Rubic:
//   mode='integrate' — «Integrere med NSF»: one call looking up the competition
//                      by NSF number. A competition card leaves the bench, flies
//                      to the NSF scoreboard and hovers until the answer arrives.
//   mode='sync'      — «Synkroniser alle»: one call per participant. Driven by
//                      the real loop in view-stage-admin, which advances
//                      `current` once per shooter. The <svg> is keyed on
//                      `current`, so each shooter remounts the scene: the
//                      scoreboard pulses for the one that just came back, the
//                      next shooter card leaves the bench and hovers by the
//                      board until NSF answers (= the next remount).
//   mode='report'    — «Rapporter resultater til NSF» (and the dry-run): one
//                      call. The shot targets pile up on the bench, fold into
//                      an envelope and fly to the board.
// Night shooting range: berm and target stands downrange on the left, the
// shooting bench in the middle, the NSF scoreboard on the right. Like the
// samlefaktura/accounting scenes the picture is locked to one look; only the
// strip below follows the page theme via Bootstrap classes.
//
// Styles live in a literal <style> inside the <svg> (same trick as the
// pull-to-refresh scenes) so preact-cli's CSS Modules hashing can't touch the
// class names.

export const NSF_SCENE_MODES = ['integrate', 'sync', 'report'];

const P = {
    backdrop: '#0f141b', star: '#dbe6f3', berm: '#243447', bermFar: '#1a2634',
    ground: '#33495f', groundLine: '#4a6480', lane: '#415d79',
    post: '#56687c', board: '#e9eef4', ring: '#2c3a49', bull: '#1d2733',
    bench: '#3a4756', benchDark: '#2b3542', benchTop: '#4a5a6d',
    sheet: '#e9eef4', line: '#aeb9c6', ink: '#2c3a49', cardStroke: '#56687c',
    envelope: '#f3e9c8', envelopeEdge: '#b08d3c',
    scoreboard: '#1b2430', scoreboardEdge: '#7f93a8', scoreboardText: '#ffc107',
    text: '#a2b8d0', accent: '#ff6b6b', badge: '#20c997', signal: '#20c997', hit: '#ff6b6b',
};

const SCENE_CSS = `
.ns-scene .ns-anim {
    transform-box: fill-box;
    transform-origin: center;
}
@keyframes nsCardIn {
    from { transform: translate(-250px, 26px); opacity: 0; }
    20%  { opacity: 1; }
    to   { transform: translate(0, 0); opacity: 1; }
}
@keyframes nsFly {
    0%   { transform: translate(0, 0) rotate(0deg); }
    50%  { transform: translate(90px, -92px) rotate(-10deg); }
    100% { transform: translate(172px, -34px) rotate(0deg); }
}
@keyframes nsHover {
    0%, 100% { transform: translate(172px, -34px); }
    50%      { transform: translate(172px, -41px); }
}
@keyframes nsTargetIn {
    from { transform: translate(-260px, -30px) rotate(-12deg); opacity: 0; }
    15%  { opacity: 1; }
    to   { transform: translate(0, 0) rotate(0deg); opacity: 1; }
}
@keyframes nsFold {
    from { transform: scaleY(1);   opacity: 1; }
    80%  { opacity: 1; }
    to   { transform: scaleY(0.2); opacity: 0; }
}
@keyframes nsEnvelopeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
}
@keyframes nsDash {
    from { stroke-dashoffset: 0; }
    to   { stroke-dashoffset: -24; }
}
@keyframes nsBlink {
    0%, 100% { opacity: 1; }
    50%      { opacity: 0.15; }
}
@keyframes nsTwinkle {
    0%, 100% { opacity: 0.9; }
    50%      { opacity: 0.25; }
}
@keyframes nsPulse {
    from { opacity: 0.9; transform: scale(0.7); }
    to   { opacity: 0;   transform: scale(1.35); }
}
@keyframes nsScan {
    from { transform: translateX(0); }
    to   { transform: translateX(96px); }
}
@media (prefers-reduced-motion: reduce) {
    .ns-scene .ns-anim { animation: none !important; }
    .ns-scene .ns-card { opacity: 1 !important; transform: translate(172px, -34px) !important; }
    .ns-scene .ns-target { opacity: 0 !important; }
    .ns-scene .ns-envelope { opacity: 1 !important; transform: translate(172px, -34px) !important; }
    .ns-scene .ns-pulse { opacity: 0 !important; }
}
`;

const STARS = [[40, 22], [120, 40], [190, 14], [280, 30], [350, 12], [470, 26], [560, 10], [740, 18], [780, 44]];

// Target stand downrange: post + board with scoring rings, optionally with a hit.
const TargetStand = ({ x, hit = false }) => (
    <g>
        <rect x={x - 3} y='118' width='6' height='40' fill={P.post} />
        <rect x={x - 20} y='78' width='40' height='40' rx='3' fill={P.board} stroke={P.post} stroke-width='1.5' />
        <circle cx={x} cy='98' r='14' fill='none' stroke={P.ring} stroke-width='2' opacity='0.85' />
        <circle cx={x} cy='98' r='8' fill='none' stroke={P.ring} stroke-width='2' opacity='0.85' />
        <circle cx={x} cy='98' r='3' fill={P.bull} />
        {hit && <circle cx={x + 3} cy='94' r='2.5' fill={P.hit} />}
    </g>
);

// Small target sheet used on cards and in the report pile.
const MiniTarget = ({ x, y, s = 1 }) => (
    <g>
        <circle cx={x} cy={y} r={9 * s} fill='none' stroke={P.ring} stroke-width='1.5' opacity='0.8' />
        <circle cx={x} cy={y} r={5 * s} fill='none' stroke={P.ring} stroke-width='1.5' opacity='0.8' />
        <circle cx={x} cy={y} r={1.8 * s} fill={P.bull} />
        <circle cx={x + 2.5 * s} cy={y - 2 * s} r={1.6 * s} fill={P.hit} />
    </g>
);

// The competition card («integrate»): bullseye + the NSF number.
const CompetitionCard = ({ nsfNumber }) => (
    <g>
        <rect x='362' y='62' width='76' height='64' rx='3' fill={P.sheet} stroke={P.cardStroke} stroke-width='1' />
        <MiniTarget x={383} y={84} />
        <rect x='400' y='74' width='30' height='4' rx='2' fill={P.ink} opacity='0.7' />
        <rect x='400' y='84' width='24' height='3' rx='1.5' fill={P.line} />
        <rect x='400' y='92' width='28' height='3' rx='1.5' fill={P.line} />
        <text x='400' y='118' text-anchor='middle' font-family='sans-serif' font-size='12' font-weight='800' fill={P.ink}>{nsfNumber}</text>
    </g>
);

// The shooter card («sync»): a head-and-shoulders silhouette with text lines.
const ShooterCard = () => (
    <g>
        <rect x='367' y='62' width='66' height='64' rx='3' fill={P.sheet} stroke={P.cardStroke} stroke-width='1' />
        <circle cx='385' cy='82' r='7' fill={P.ink} opacity='0.75' />
        <path d='M373 104 a12 10 0 0 1 24 0 Z' fill={P.ink} opacity='0.75' />
        <rect x='402' y='76' width='24' height='4' rx='2' fill={P.ink} opacity='0.7' />
        <rect x='402' y='86' width='20' height='3' rx='1.5' fill={P.line} />
        <rect x='402' y='94' width='24' height='3' rx='1.5' fill={P.line} />
        <rect x='376' y='114' width='46' height='4' rx='2' fill={P.line} />
    </g>
);

const Badge = ({ x, y, fill, count, label, countClass }) => (
    <g>
        <circle cx={x} cy={y} r='15' fill={fill} />
        <text class={countClass} x={x} y={y + 5} text-anchor='middle' font-family='sans-serif' font-size='14' font-weight='700' fill='#ffffff'>{count}</text>
        <text x={x} y={y + 30} text-anchor='middle' font-family='sans-serif' font-size='10' font-weight='600' fill={P.text}>{label}</text>
    </g>
);

const REPORT_TARGETS = [0, 1, 2];
const FLY_DELAY = { card: 0.7, envelope: 1.75 };

const NsfTransferProgress = ({
    mode = 'sync', current = 0, total = 0, label = '', found = 0, notFound = 0,
    nsfNumber = '', dryRun = false,
}) => {
    const isSync = mode === 'sync';
    const isReport = mode === 'report';
    const safeCurrent = Math.max(current, 0);
    const safeFound = Math.max(found, 0);
    const safeNotFound = Math.max(notFound, 0);
    const inFlight = !isSync || safeCurrent < total;
    const pct = isSync && total > 0 ? Math.round((Math.min(safeCurrent, total) / total) * 100) : 100;
    const showPulse = isSync && safeCurrent > 0;

    let title = 'Rapporterer resultater til NSF';
    if (mode === 'integrate') {
        title = `Henter stevne ${nsfNumber} fra NSF`;
    } else if (isSync) {
        title = 'Synkroniserer deltakere med NSF';
    } else if (dryRun) {
        title = 'Dry-run: bygger rapporten uten å sende';
    }

    let stripLabel = label;
    if (!isSync) {
        stripLabel = title;
    }

    return (
        <div class='mt-2 rounded-3 overflow-hidden border'>
            <svg
                key={isSync ? safeCurrent : mode}
                viewBox='0 0 800 230'
                preserveAspectRatio='xMidYMid meet'
                role='img'
                class='ns-scene'
                style={`display: block; width: 100%; height: auto; max-height: 260px; overflow: visible; background: ${P.backdrop};`}
            >
                <style>{SCENE_CSS}</style>
                <title>{title}</title>

                {/* Night sky */}
                {STARS.map(([sx, sy], i) => (
                    <circle
                        key={`s${sx}`}
                        class='ns-anim'
                        cx={sx}
                        cy={sy}
                        r={i % 3 === 0 ? 1.8 : 1.2}
                        fill={P.star}
                        style={`animation: nsTwinkle ${2 + (i % 4) * 0.7}s ease-in-out ${(i % 5) * 0.4}s infinite;`}
                    />
                ))}

                {/* Berm downrange */}
                <path d='M-20 160 L-20 110 Q 120 84 260 104 T 520 100 T 820 106 L 820 160 Z' fill={P.bermFar} />
                <path d='M-20 170 L-20 128 Q 140 108 300 122 T 560 118 T 820 124 L 820 170 Z' fill={P.berm} />

                {/* Range floor with lane marks */}
                <rect x='0' y='158' width='800' height='72' fill={P.ground} />
                <rect x='0' y='158' width='800' height='4' fill={P.groundLine} opacity='0.6' />
                {Array.from({ length: 20 }, (_, i) => (
                    <rect key={i} x={i * 40 + 8} y='192' width='20' height='4' fill={P.lane} opacity='0.8' />
                ))}

                {/* Dashed flight path from the bench to the scoreboard */}
                <path
                    class='ns-anim'
                    d='M400 100 Q 470 -10 572 72'
                    fill='none'
                    stroke={P.scoreboardEdge}
                    stroke-width='2'
                    stroke-dasharray='6 6'
                    opacity='0.55'
                    style='animation: nsDash 0.8s linear infinite;'
                />

                {/* Target stands downrange */}
                <TargetStand x={90} hit />
                <TargetStand x={150} hit={isReport} />
                <TargetStand x={210} hit />

                {/* Shooting bench */}
                <rect x='340' y='130' width='120' height='10' rx='3' fill={P.benchTop} />
                <rect x='346' y='140' width='108' height='34' rx='4' fill={P.bench} />
                <rect x='356' y='150' width='60' height='4' rx='2' fill={P.benchDark} />
                <rect x='352' y='174' width='8' height='14' fill={P.benchDark} />
                <rect x='440' y='174' width='8' height='14' fill={P.benchDark} />
                <circle class='ns-anim' cx='440' cy='156' r='5' fill={P.signal} style='animation: nsBlink 1.2s ease-in-out infinite;' />

                {inFlight && !isReport && (
                    <g
                        class='ns-card ns-anim'
                        style={`animation: nsCardIn 0.5s ease-out both, nsFly 1.3s ease-in-out ${FLY_DELAY.card}s forwards, nsHover 1.8s ease-in-out ${FLY_DELAY.card + 1.3}s infinite;`}
                    >
                        {mode === 'integrate' ? <CompetitionCard nsfNumber={nsfNumber} /> : <ShooterCard />}
                    </g>
                )}

                {isReport && (
                    <g>
                        {/* Shot targets fly in from the stands and pile up on the bench, then fold */}
                        {REPORT_TARGETS.map(i => (
                            <g
                                key={i}
                                class='ns-target ns-anim'
                                style={`transform-origin: center bottom; animation: nsTargetIn 0.55s ease-out ${i * 0.3}s both, nsFold 0.35s ease-in 1.35s forwards;`}
                            >
                                <rect x={364 + i * 4} y={66 - i * 3} width='66' height='60' rx='2' fill={P.sheet} stroke={P.cardStroke} stroke-width='1' />
                                <MiniTarget x={397 + i * 4} y={92 - i * 3} s={1.6} />
                                <rect x={374 + i * 4} y={114 - i * 3} width='46' height='4' rx='2' fill={P.line} />
                            </g>
                        ))}

                        {/* The envelope: appears on the bench, flies the arc, hovers by the board */}
                        <g
                            class='ns-envelope ns-anim'
                            style={`opacity: 0; animation: nsEnvelopeIn 0.25s ease-out 1.6s forwards, nsFly 1.3s ease-in-out ${FLY_DELAY.envelope}s forwards, nsHover 1.8s ease-in-out ${FLY_DELAY.envelope + 1.3}s infinite;`}
                        >
                            <rect x='376' y='92' width='48' height='32' rx='3' fill={P.envelope} stroke={P.envelopeEdge} stroke-width='1.5' />
                            <path d='M376 92 L400 112 L424 92' fill='none' stroke={P.envelopeEdge} stroke-width='1.5' />
                            <rect x='412' y='96' width='8' height='8' fill={P.accent} opacity='0.8' />
                        </g>
                    </g>
                )}

                {/* NSF scoreboard with antenna */}
                <rect x='604' y='108' width='8' height='60' fill={P.post} />
                <rect x='692' y='108' width='8' height='60' fill={P.post} />
                <line x1='652' y1='40' x2='652' y2='26' stroke={P.post} stroke-width='2' />
                <circle class='ns-anim' cx='652' cy='24' r='4' fill={P.signal} style='animation: nsBlink 1.2s ease-in-out 0.6s infinite;' />
                {showPulse && (
                    <ellipse
                        class='ns-pulse ns-anim'
                        cx='652'
                        cy='76'
                        rx='70'
                        ry='40'
                        fill='none'
                        stroke={P.signal}
                        stroke-width='3'
                        style='opacity: 0; animation: nsPulse 0.7s ease-out 0.05s forwards;'
                    />
                )}
                <rect x='592' y='40' width='120' height='72' rx='6' fill={P.scoreboardEdge} />
                <rect x='596' y='44' width='112' height='64' rx='4' fill={P.scoreboard} />
                <text x='652' y='78' text-anchor='middle' font-family='sans-serif' font-size='26' font-weight='800' letter-spacing='3' fill={P.scoreboardText}>NSF</text>
                {/* Scanning light along the bottom of the board */}
                <rect x='604' y='98' width='96' height='3' rx='1.5' fill={P.post} opacity='0.6' />
                <rect class='ns-anim' x='604' y='98' width='16' height='3' rx='1.5' fill={P.scoreboardText} style='animation: nsScan 1.6s ease-in-out infinite alternate;' />

                {/* Counters */}
                {isSync && (
                    <g>
                        <Badge x={606} y={132} fill={P.badge} count={safeFound} label='Funnet' countClass='ns-found-count' />
                        {safeNotFound > 0 && (
                            <Badge x={700} y={132} fill={P.accent} count={safeNotFound} label='Ikke funnet' countClass='ns-notfound-count' />
                        )}
                    </g>
                )}

                {/* Labels */}
                <text x='150' y='220' text-anchor='middle' font-family='sans-serif' font-size='12' font-weight='600' fill={P.text}>Skivene</text>
                <text x='400' y='220' text-anchor='middle' font-family='sans-serif' font-size='12' font-weight='600' fill={P.text}>Standplass</text>
                <text x='652' y='220' text-anchor='middle' font-family='sans-serif' font-size='12' font-weight='600' fill={P.text}>NSF / Rubic</text>
            </svg>

            <div class='px-3 pb-3 bg-body text-body'>
                <div class='text-center fw-bold pt-2' style='min-height: 1.5em;'>
                    {stripLabel}
                </div>
                <div class='progress mt-2' style='height: 10px;'>
                    <div
                        class='progress-bar bg-success progress-bar-striped progress-bar-animated'
                        role='progressbar'
                        style={`width: ${pct}%;`}
                        aria-valuenow={isSync ? safeCurrent : undefined}
                        aria-valuemin={isSync ? '0' : undefined}
                        aria-valuemax={isSync ? total : undefined}
                    />
                </div>
                <div class='d-flex justify-content-between small mt-1 text-body-secondary'>
                    {isSync ? (
                        <>
                            <span><i class='fa-solid fa-spinner fa-spin' /> Synkroniserer {safeCurrent} av {total}</span>
                            <span>{safeFound} funnet{safeNotFound > 0 ? `, ${safeNotFound} ikke funnet` : ''}</span>
                        </>
                    ) : (
                        <span><i class='fa-solid fa-spinner fa-spin' /> Venter på svar fra NSF…</span>
                    )}
                </div>
                {mode !== 'integrate' && (
                    <div class='alert alert-warning py-1 mt-2 mb-0 small text-center'>
                        <i class='fa-solid fa-triangle-exclamation' /> Ikke lukk vinduet før overføringen er ferdig.
                    </div>
                )}
            </div>
        </div>
    );
};

export default NsfTransferProgress;
